import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, alpha } from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axios';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import Header from '../../common/Header';
import ReceiptIcon from '@mui/icons-material/Receipt';

const NEON = '#38BDF8';
const NEON3 = '#34D399';

/**
 * Return URL after Stripe Checkout success — confirms payment with the API.
 */
export default function ClaimStripeReturn() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Missing Stripe session. Return to the claim and try again.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await axiosInstance.post(`/claims/${id}/stripe/complete`, {
          session_id: sessionId,
        });
        if (cancelled) return;
        setStatus('success');
        setMessage(res.data?.message || 'Payment confirmed. Claim marked as paid.');
        setTimeout(() => navigate(`/claims/${id}`, { replace: true }), 2500);
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setMessage(
          err.response?.data?.message ||
            'Could not confirm Stripe payment. Check the claim status or contact an admin.'
        );
      }
    })();

    return () => { cancelled = true; };
  }, [id, sessionId, navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
      <Header
        title="Stripe payment"
        subtitle="Confirming demo reimbursement"
        icon={ReceiptIcon}
        showBackButton
        onBackClick={() => navigate(`/claims/${id}`)}
      />

      {status === 'loading' && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Loading message="Confirming Stripe payment…" size={48} variant="primary" />
        </Box>
      )}

      {status === 'success' && (
        <FeedbackMessage
          message={message}
          severity="success"
          sx={{
            mt: 3,
            background: alpha(NEON3, 0.08),
            border: `1px solid ${alpha(NEON3, 0.35)}`,
            color: NEON3,
          }}
        />
      )}

      {status === 'error' && (
        <>
          <FeedbackMessage message={message} severity="error" sx={{ mt: 3 }} />
          <Typography sx={{ mt: 2, fontSize: '0.85rem', color: alpha('#94A3B8', 0.9) }}>
            You can close this page and open the claim again from Claims.
          </Typography>
        </>
      )}
    </Container>
  );
}
